import utilSvc from '../../utilSvc';
import type { DateRangeMode } from './dateRangeModesConfig';
import { getModeMeta } from './dateRangeModesConfig';

export const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

export const parseLocalDate = (val?: string): Date => {
    if (!val) return new Date();
    // Parse YYYY-MM-DD as local midnight
    const parts = val.split('-').map(Number);
    if (parts.length === 3) {
        return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    return new Date(val);
};

// Returns error message for custom 3 date range, or empty string if valid
export function getCustom3DateRangeError(startDate: string, endDate: string): string {
    if (!isValidDateString(startDate) || !isValidDateString(endDate)) return '';
    const start = utilSvc.dt.zeroTime(parseLocalDate(startDate));
    const end = utilSvc.dt.zeroTime(parseLocalDate(endDate));
    const today = utilSvc.dt.zeroTime(new Date());
    const diffDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    if (start > end) {
        return 'Start Date must be before or equal to End Date.';
    } else if (start > today) {
        return 'Start date cannot be in the future.';
    } else if (end > today) {
        return 'End date cannot be in the future.';
    } else if (diffDays > 3) {
        return 'Date range cannot exceed 3 days.';
    }
    return '';
}

// Returns error message for custom 92 date range, or empty string if valid
export function getCustom92DateRangeError(startDate: string, endDate: string): string {
    if (!isValidDateString(startDate) || !isValidDateString(endDate)) return '';
    const start = utilSvc.dt.zeroTime(parseLocalDate(startDate));
    const end = utilSvc.dt.zeroTime(parseLocalDate(endDate));
    const today = utilSvc.dt.zeroTime(new Date());
    const diffDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    if (start > end) {
        return 'Start Date must be before or equal to End Date.';
    } else if (start > today) {
        return 'Start date cannot be in the future.';
    } else if (end > today) {
        return 'End date cannot be in the future.';
    } else if (diffDays > 92) {
        return 'Date range cannot exceed 92 days.';
    }
    return '';
}
/**
 * Utility functions for ValidInputDate component
 */

// VALIDATION HELPERS
export function isValidDateString(val: any): boolean {
    if (!val || typeof val !== 'string') {
        return false;
    }

    // Check format: YYYY-MM-DD
    if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) {
        return false;
    }

    // Parse as local time using Date(year, month-1, day)
    const [year, month, day] = val.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime())) {
        return false;
    }

    // Verify that the parsed date matches the input (catches invalid dates like Feb 30)
    return date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day;
}

export function isDateInRange(val: string, min?: string, max?: string): boolean {
    if (!isValidDateString(val)) return false;

    const date = new Date(val).getTime();
    if (min) {
        const minDate = new Date(min).getTime();
        if (minDate > date) return false;
    }
    if (max) {
        const maxDate = new Date(max).getTime();
        if (maxDate < date) return false;
    }

    return true;
}

// CALENDAR HELPERS
export interface DayObject {
    date: Date;
    dayOfMonth: number;
    isCurrentMonth: boolean;
    isToday: boolean;
    isSelected: boolean;
    isDisabled: boolean;
}

export function generateCalendarDays(
    displayDate: Date,
    selectedDateStr: string,
    min?: string,
    max?: string,
): DayObject[][] {
    const year = displayDate.getFullYear();
    const month = displayDate.getMonth();

    // Get first day of month and number of days in month
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday

    // Adjust for Monday start (0 = Sunday, we want Monday = 0)
    const adjustedStart = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const weeks: DayObject[][] = [];
    let currentWeek: DayObject[] = [];

    // Add padding days from previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = adjustedStart - 1; i >= 0; i--) {
        const dayNum = prevMonthLastDay - i;
        const date = new Date(year, month - 1, dayNum);
        currentWeek.push({
            date,
            dayOfMonth: dayNum,
            isCurrentMonth: false,
            isToday: false,
            isSelected: false,
            isDisabled: true,
        });
    }

    // Add days of current month
    for (let dayOfMonth = 1; dayOfMonth <= daysInMonth; dayOfMonth++) {
        const date = new Date(year, month, dayOfMonth);
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayOfMonth).padStart(2, '0')}`;

        let isDisabled = false;
        if (min && !isDateInRange(dateStr, min)) {
            isDisabled = true;
        }
        if (max && !isDateInRange(dateStr, undefined, max)) {
            isDisabled = true;
        }

        currentWeek.push({
            date,
            dayOfMonth,
            isCurrentMonth: true,
            isToday: dateStr === todayStr,
            isSelected: dateStr === selectedDateStr,
            isDisabled,
        });

        if (currentWeek.length === 7) {
            weeks.push(currentWeek);
            currentWeek = [];
        }
    }

    // Add padding days from next month
    let nextMonthDay = 1;
    while (currentWeek.length < 7 && currentWeek.length > 0) {
        const date = new Date(year, month + 1, nextMonthDay);
        currentWeek.push({
            date,
            dayOfMonth: nextMonthDay,
            isCurrentMonth: false,
            isToday: false,
            isSelected: false,
            isDisabled: true,
        });
        nextMonthDay++;
    }

    if (currentWeek.length > 0) {
        weeks.push(currentWeek);
    }

    return weeks;
}

export function formatDateForDisplay(dateStr: string): string {
    if (!isValidDateString(dateStr)) {
        return '';
    }
    return dateStr; // Already in YYYY-MM-DD format
}

export function getMonthYearDisplay(date: Date): string {
    return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

// DATE RANGE CALCULATIONS
export interface DateRangeConstraints {
    minDate?: string; // YYYY-MM-DD format
    maxDate?: string; // YYYY-MM-DD format
    keepDates?: boolean;
}

export interface DateRangeResult {
    startDate: string; // YYYY-MM-DD format or empty string for NONE
    endDate: string; // YYYY-MM-DD format or empty string for NONE
}

/**
 * Helper function to get zero-time date (midnight UTC)
 */
export function getZeroTimeDate(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

/**
 * Helper function to format date to YYYY-MM-DD string
 */
function dateToString(date: Date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Constrain a date to be within min and max bounds
 */
function constrainDate(date: Date, minDate?: string, maxDate?: string): Date {
    let result = new Date(date);

    if (minDate && isValidDateString(minDate)) {
        const min = new Date(minDate + 'T00:00:00Z');
        if (result < min) {
            result = new Date(min);
        }
    }

    if (maxDate && isValidDateString(maxDate)) {
        const max = new Date(maxDate + 'T00:00:00Z');
        if (result > max) {
            result = new Date(max);
        }
    }

    return result;
}

/**
 * Calculate start and end dates based on the selected date range mode
 * @param mode The date range mode (TODAY, YESTERDAY, 2DAYS, 3DAYS, TODAYYESTERDAY, CUSTOM3, NONE)
 * @param constraints Optional min/max date constraints
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 */
export function calculateDateRange(mode: DateRangeMode, constraints?: DateRangeConstraints): DateRangeResult {
    if (mode === 'NONE') {
        return { startDate: '', endDate: '' };
    }
    const today = getZeroTimeDate(new Date());
    const meta = getModeMeta(mode);
    // If mode is not found, throw
    if (!meta || !meta.getRange || meta.mode !== mode) {
        throw new Error(`calculateDateRange: Unknown or unsupported date range mode: ${mode}`);
    }
    let { start, end } = meta.getRange({ today });

    // Apply constraints
    if (constraints) {
        start = constrainDate(start, constraints.minDate, constraints.maxDate);
        end = constrainDate(end, constraints.minDate, constraints.maxDate);
        if (start > end) {
            start = new Date(end);
        }
    }
    return {
        startDate: dateToString(start),
        endDate: dateToString(end),
    };
}

// General custom date range error helper for tests and UI
export function getCustomDateRangeError(startDate: string, endDate: string, maxDays: number = 3): string {
    if (maxDays === 3) return getCustom3DateRangeError(startDate, endDate);
    if (maxDays === 92) return getCustom92DateRangeError(startDate, endDate);
    // fallback: just check order
    if (!isValidDateString(startDate) || !isValidDateString(endDate)) return '';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    if (start > end) {
        return 'Start Date must be before or equal to End Date.';
    } else if (diffDays > maxDays) {
        return `Date range cannot exceed ${maxDays} days.`;
    }
    return '';
}

// Helper to format a Date as YYYY-MM-DD (local time)
export function formatLocalDate(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

// Calculate start and end date for a given mode
export function getDateRangeForMode(mode: string): { start: string; end: string } {
    // Use UTC zero-time for consistency with calculateDateRange
    const today = getZeroTimeDate(new Date());
    const meta = getModeMeta(mode as DateRangeMode);
    if (!meta || !meta.getRange) {
        // Fallback: today
        const todayStr = formatLocalDate(new Date());
        return { start: todayStr, end: todayStr };
    }
    const { start, end } = meta.getRange({ today });
    return {
        start: formatLocalDate(start),
        end: formatLocalDate(end),
    };
}

// Get today's date in YYYY-MM-DD (local time)
export function getLocalToday(): string {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
